import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import {
  buildErasureDispositionPlan,
  type ErasureDispositionPlan,
} from './erasureDispositionModel';
import type { User } from '../dataProvider/types';

/**
 * The per-member erasure disposition plan, assembled through the seam (`buildErasureDispositionPlan`),
 * NOT computed in the component. It reads the member's record COUNTS across the inventory (the same
 * collections the DSAR export reads, here counted rather than dumped) and applies the disposition
 * policy. READ-ONLY: it only ever reads; the execute action is the only thing that mutates.
 */

const COUNT = {
  pagination: { page: 1, perPage: 100_000 },
  sort: { field: 'id', order: 'ASC' as const },
};

/** The collections counted for the plan, keyed by the policy key, with their member-link field. */
const COUNTED: ReadonlyArray<{ key: string; resource: string; field: string }> =
  [
    { key: 'transactions', resource: 'transactions', field: 'userId' },
    { key: 'cashoutRequests', resource: 'cashoutRequests', field: 'userId' },
    { key: 'balanceLedger', resource: 'balanceLedger', field: 'userId' },
    {
      key: 'welcomeBonusFundings',
      resource: 'welcomeBonusFundings',
      field: 'userId',
    },
    { key: 'sanctionsMatches', resource: 'sanctionsMatches', field: 'userId' },
    { key: 'consents', resource: 'consents', field: 'userId' },
    { key: 'tickets', resource: 'tickets', field: 'userId' },
    { key: 'dsarRequests', resource: 'dsarRequests', field: 'subjectUserId' },
    { key: 'clicks', resource: 'clicks', field: 'userId' },
  ];

export const useErasureDisposition = (
  subjectUserId: number,
): { plan: ErasureDispositionPlan | null; isLoading: boolean } => {
  const dataProvider = useDataProvider();

  const { data, isLoading } = useQuery({
    queryKey: ['erasure-disposition', subjectUserId],
    queryFn: async () => {
      const user = (await dataProvider.getOne('users', { id: subjectUserId }))
        .data as unknown as User;
      const results = await Promise.all(
        COUNTED.map((c) =>
          dataProvider.getList(c.resource, {
            ...COUNT,
            filter: { [c.field]: subjectUserId },
          }),
        ),
      );
      const counts: Record<string, number> = {};
      COUNTED.forEach((c, i) => {
        counts[c.key] = results[i].data.length;
      });
      return buildErasureDispositionPlan({ user, counts });
    },
  });

  return { plan: data ?? null, isLoading };
};
